import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import PaperButton from "components/buttons/PaperButton";
import CustomLink from "components/CustomLink";
import CustomTable from "components/CustomTable";
import ExportReactCSV from "components/ExportReactCSV";
import SearchComponent, { searchDataFc } from "components/SearchComponent";
import { CustomStatusBadge } from "components/StatusBadge";
import CustomText from "components/typography/CustomText";
import { getWeekDate, getWeekDaysByWeekNumber } from "lib/date";
import { currency } from "lib/formatter";
import { orderStatusOptions, paymentStatusOption } from "lib/global";
import { useMemo, useState } from "react";

const headers = [
  { label: "Order ID", key: "id" },
  { label: "Order Type", key: "box_type" },
  { label: "Payment Date", key: "payment_date" },
  { label: "Dispatch Date", key: "dispatch_date" },
  { label: "Amount", key: "amount" },
  { label: "Paid", key: "paid" },
  { label: "Box Status", key: "box_status" },
];

export default function InvoiceTable({
  data = [],
  onView = () => {},
  title = "",
}) {
  const [status, setStatus] = useState("all");
  const [searchWord, setSearchWord] = useState("");
  const [payment, setPayment] = useState("all");
  const [searchType, setSearchType] = useState("all");

  const searchData = useMemo(() => {
    let filteredData = data;
    if (searchWord) {
      filteredData = filteredData.filter((row) => {
        return searchDataFc({ row, searchType, searchWord });
      });
    }
    filteredData = filteredData?.filter((item) =>
      payment === "all" ? filteredData : item?.paid === payment
    );
    return filteredData.filter((item) =>
      status === "all" ? filteredData : item?.status === status
    );
    //eslint-disable-next-line
  }, [status, searchType, payment, searchWord, data]);

  const subscriptionPayments = () => {
    let custs = data.map((row) => ({
      id: row?.id ?? "",
      box_type: row?.box_type ? "Sub" : "One-Off",
      payment_date: getWeekDaysByWeekNumber({
        weekNumber: Boolean(row?.week_next_num)
          ? row?.week_next_num - 2
          : row?.week_num - 2,
        addDay: 6,
        format: "DD/MM/YY",
        yearNumber: row?.week_year,
      }),
      dispatch_date: getWeekDaysByWeekNumber({
        weekNumber: Boolean(row?.week_next_num)
          ? row?.week_next_num
          : row?.week_num,
        addDay: row?.week_day - 1,
        format: "DD/MM/YY",
        yearNumber: row?.week_year,
      }),
      amount: currency.format(row.amount),
      paid: paymentStatusOption.find((item) => item.id === (row?.paid ?? "0"))
        ?.label,
      box_status: orderStatusOptions.find(
        (item) => item.id === (row?.status ?? "0")
      )?.label,
    }));
    return custs;
  };

  const handleSearchWord = (e) => {
    setSearchWord(e.target.value.trim().replace(/[^A-Za-z0-9@. ]/g, ""));
  };

  return (
    <div>
      <CustomTable
        height={500}
        pageCount={50}
        toolbar={
          <div className="p-4 border-b border-white-gray flex justify-between items-center">
            <div className="flex items-center flex-wrap">
              <CustomText className="pr-3" variant="h6" bold={600}>
                All Orders
              </CustomText>
              <SearchComponent
                searchWord={searchWord}
                placeholder="Search Payment"
                searchType={searchType}
                onKeyDown={(e) => handleSearchWord(e)}
                onBlur={handleSearchWord}
                onChange={(e) => {
                  setSearchType(e.target.value);
                  // setSearchWord("");
                }}
                onChangeSearchWord={handleSearchWord}
              />
              <FormControl sx={{ mx: 1, minWidth: 100, background: "#fff" }}>
                <InputLabel className="bold text-sm mt-0.5">Status</InputLabel>{" "}
                <Select
                  label="Status"
                  size="small"
                  sx={{ background: "#fff", width: 160 }}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {orderStatusOptions?.map((item) => (
                    <MenuItem value={item?.id} key={item?.id}>
                      {item?.label ?? ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mr: 1, minWidth: 100, background: "#fff" }}>
                <InputLabel className="bold text-sm mt-0.5">
                  Payment Status
                </InputLabel>
                <Select
                  label="Payment Status"
                  size="small"
                  sx={{ background: "#fff", width: 160 }}
                  value={payment}
                  onChange={(e) => setPayment(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {paymentStatusOption?.map((item) => (
                    <MenuItem value={item?.id} key={item?.id}>
                      {item?.label ?? ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <ExportReactCSV
              csvHeaders={headers}
              csvData={subscriptionPayments()}
              fileName={`subscriptionPayments.csv`}
            />
          </div>
        }
        data={searchData}
        columns={[
          {
            label: "ORDER ID",
            selector: "id",
            cell: (row) => (
              <CustomLink to={`/order/${row?.id}`}>
                <CustomText color="primary" variant="custom13">
                  {row?.id}
                </CustomText>
              </CustomLink>
            ),
          },
          {
            label: "ORDER TYPE",
            selector: "box_type",
            cell: (row) => (
              <CustomText color="secondary" variant="custom13">
                {row?.box_type ? "Sub" : "One-Off"}
              </CustomText>
            ),
          },
          {
            label: "PAYMENT DATE",
            selector: "date",
            sortValue: (row) =>
              getWeekDate({
                weekNumber: Boolean(row?.week_next_num)
                  ? row?.week_next_num - 2
                  : row?.week_num - 2,
                addDay: 6,
                yearNumber: row?.week_year,
              }).unix(),
            cell: (row) => (
              <CustomText color="secondary" variant="custom13">
                {getWeekDaysByWeekNumber({
                  weekNumber: Boolean(row?.week_next_num)
                    ? row?.week_next_num - 2
                    : row?.week_num - 2,
                  addDay: 6,
                  format: "DD/MM/YY",
                  yearNumber: row?.week_year,
                })}
              </CustomText>
            ),
          },
          {
            label: "DISPATCH DATE",
            selector: "date",
            sortValue: (row) =>
              getWeekDate({
                weekNumber: Boolean(row?.week_next_num)
                  ? row?.week_next_num
                  : row?.week_num,
                addDay: row?.week_day - 1,
                yearNumber: row?.week_year,
              }).unix(),
            cell: (row) => (
              <CustomText
                className="text-13"
                color="secondary"
                variant="custom13"
              >
                {getWeekDaysByWeekNumber({
                  weekNumber: Boolean(row?.week_next_num)
                    ? row?.week_next_num
                    : row?.week_num,
                  addDay: row?.week_day - 1,
                  format: "DD/MM/YY",
                  yearNumber: row?.week_year,
                })}
              </CustomText>
            ),
          },
          {
            label: "AMOUNT",
            selector: "amount",
            cell: (row) => (
              <CustomText
                className="text-13"
                color="secondary"
                variant="custom13"
              >
                {currency.format(row.amount)}
              </CustomText>
            ),
          },
          {
            label: "PAID",
            selector: "paid",
            sortValue: (row) =>
              paymentStatusOption.find((item) => item.id === (row?.paid ?? "0"))
                ?.label,
            cell: (row) => (
              <CustomStatusBadge
                size="body2"
                text={
                  paymentStatusOption.find(
                    (item) => item.id === (row?.paid ?? "0")
                  )?.label
                }
                variant={
                  paymentStatusOption.find(
                    (item) => item.id === (row?.paid ?? "0")
                  )?.variant
                }
              />
            ),
          },
          {
            label: "BOX STATUS",
            selector: "status",
            sortValue: (row) =>
              orderStatusOptions.find(
                (item) =>
                  item.id ===
                  (row?.tracking_info?.consignmentId ? "1" : row?.status ?? "0")
              )?.label ?? "",
            cell: (row) => (
              <CustomStatusBadge
                size="body2"
                text={
                  orderStatusOptions.find(
                    (item) =>
                      item.id ===
                      (row?.tracking_info?.consignmentId
                        ? "1"
                        : row?.status ?? "0")
                  )?.label
                }
                variant={
                  orderStatusOptions.find(
                    (item) =>
                      item.id ===
                      (row?.tracking_info?.consignmentId
                        ? "1"
                        : row?.status ?? "0")
                  )?.variant
                }
              />
            ),
          },
          {
            label: " ",
            selector: "actions",
            cell: (row, rowIndex) => (
              <CustomLink to={`/order/${row?.id}`}>
                <PaperButton
                  className="text-13"
                  variant="text"
                  color="primary"
                  // onClick={() => onView(row, rowIndex)}
                >
                  View Details
                </PaperButton>
              </CustomLink>
            ),
          },
        ]}
        // onSelect={(row) => history.push(`/order/${row?.id}`)}
      />
    </div>
  );
}
